import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { TfiLayoutGrid2Alt } from "react-icons/tfi";
import { TbHomeSearch } from "react-icons/tb";
import { RiBarChart2Fill, RiFolderSettingsLine } from "react-icons/ri";
import { Stepper, Step, StepLabel, styled } from "@mui/material";
import { useReportSidebarData } from "../../../components/shared/useReportSidebarData";
import useReportStepperStore from "./reportSidebarStore";
import logo from "../../../assets/dronejtek-log.svg";
import { useTranslation } from "react-i18next";

const CustomStepIconRoot = styled("div")(({ ownerState }) => ({
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: ownerState.active
    ? "#3D6C50"
    : ownerState.disabled
      ? "#b9bdc5"
      : "#D1F5C9",
  border: ownerState.active ? "2px solid #3D6C50" : "none",
  color: ownerState.active
    ? "#fff"
    : ownerState.disabled
      ? "#545960"
      : "#70C445",
  zIndex: 1,
  opacity: ownerState.disabled ? 0.5 : 1,
}));

const CustomStepLabel = styled(StepLabel)({
  "& .MuiStepLabel-label": {
    fontSize: "14px",
    color: "#9CA3AF",
    "&.Mui-active": {
      color: "#70C445",
      fontWeight: 500,
    },
    "&.Mui-completed": {
      color: "#fff",
      fontWeight: 500,
    },
    "&.Mui-disabled": {
      color: "#6B7280",
      opacity: 0.6,
    },
  },
});

function getStepAccess(step) {
  if (typeof step.access === "boolean") return step.access;
  if (Array.isArray(step.tab) && step.tab.length > 0)
    return !!step.tab[0].access;
  if (Array.isArray(step.option) && step.option.length > 0)
    return getStepAccess(step.option[0]);
  return false;
}

function getStepPath(step, parentStep) {
  if (parentStep && parentStep.step === "buildingOverview" && step.id) {
    return `/dashboard/reports/building-overview/element/${step.id}`;
  }
  if (step.step === "information") return "/dashboard/reports/information";
  if (step.step === "buildingOverview")
    return "/dashboard/reports/building-overview";
  if (step.step === "financialOverview")
    return "/dashboard/reports/financial-overview";
  if (step.step === "maintenance") return "/dashboard/reports/maintenance";
  return `/dashboard/reports/${step.id || step.step || "unknown"}`;
}

const ReportSidebar = () => {
  const { t } = useTranslation();
  const iconMap = {
    information: {
      icon: <TfiLayoutGrid2Alt size={12} />,
      text: t("main.sidebar.information"),
    },
    buildingOverview: {
      icon: <TbHomeSearch size={14} />,
      text: t("main.sidebar.building-overview"),
    },
    financialOverview: {
      icon: <RiBarChart2Fill size={14} />,
      text: t("main.sidebar.financial-overview"),
    },
    maintenance: {
      icon: <RiFolderSettingsLine size={14} />,
      text: t("main.sidebar.maintenance"),
    },
  };
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const overviewId = searchParams.get("overviewId");
  useReportSidebarData(overviewId);
  const sidebarData = useReportStepperStore((s) => s.sidebarData);
  const navigate = useNavigate();
  const isStepActive = (step, parentStep) => {
    const path = getStepPath(step, parentStep);
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };
  const { i18n } = useTranslation();

  const renderSteps = (steps, level = 0, parentStep = null) => {
    if (!Array.isArray(steps)) return null;
    return steps.map((step, idx) => {
      const disabled = !getStepAccess(step);
      const active = isStepActive(step, parentStep);
      const handleClick = () => {
        if (!disabled)
          navigate(getStepPath(step, parentStep) + location.search);
      };
      return (
        <Step
          key={step.id || step.step || idx}
          completed={false}
          disabled={disabled}
        >
          <div
            className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
            onClick={handleClick}
          >
            <CustomStepLabel
              StepIconComponent={() => (
                <CustomStepIconRoot
                  ownerState={{
                    active,
                    completed: false,
                    disabled,
                  }}
                >
                  {iconMap[step.step]?.icon || <TfiLayoutGrid2Alt size={12} />}
                </CustomStepIconRoot>
              )}
            >
              <span
                className={`
          flex items-center gap-1
          ${active ? "font-bold text-textSecondaryColor" : ""}
          ${!active && !disabled ? "text-textSecondaryColor/90" : ""}
          ${disabled ? "text-gray-600" : ""}
          ${level > 0 ? "text-xs" : "text-sm"}
            `}
              >
                {iconMap[step.step]?.text || step.label || step.step}
              </span>
            </CustomStepLabel>
          </div>
          {/* Render substeps (options) if present */}
          {Array.isArray(step.option) && step.option.length > 0 && (
            <div className="relative pl-7 mt-1">
              {/* Vertical line connecting parent to options, aligned with icons */}
              <div
                className="absolute left-[14px] top-[-12px] bottom-0 w-0.5 bg-gray-300 z-0"
                style={{ borderRadius: "1px" }}
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1 relative z-10">
                {step.option.map((substep, subIdx) => {
                  const subDisabled = !getStepAccess(substep);
                  const subActive = isStepActive(substep, step);
                  const subHandleClick = () => {
                    if (!subDisabled)
                      navigate(getStepPath(substep, step) + location.search);
                  };
                  return (
                    <div
                      key={substep.id || substep.step || subIdx}
                      className="relative flex items-center group"
                    >
                      {/* Horizontal connector for all but the first option */}

                      <div
                        className="absolute left-[-12px] top-1/2 w-7 h-0.5 bg-gray-300 z-0"
                        style={{ transform: "translateY(-50%)" }}
                        aria-hidden="true"
                      />

                      <div
                        className={
                          subDisabled ? "cursor-not-allowed" : "cursor-pointer"
                        }
                        onClick={subHandleClick}
                      >
                        <CustomStepLabel
                          StepIconComponent={() => (
                            <CustomStepIconRoot
                              ownerState={{
                                active: subActive,
                                completed: false,
                                disabled: subDisabled,
                              }}
                            >
                              {iconMap[substep.step]?.icon || (
                                <TfiLayoutGrid2Alt size={12} />
                              )}
                            </CustomStepIconRoot>
                          )}
                        >
                          <span
                            className={` 
                              flex items-center gap-1 
                              ${subActive ? "font-bold text-textSecondaryColor" : ""} 
                              ${!subActive && !subDisabled ? "text-textSecondaryColor/90" : ""} 
                              ${subDisabled ? "text-gray-600" : ""} 
                              text-xs 
                           `}
                          >
                            {((text) =>
                              text.toLowerCase() === "roof"
                                ? i18n.language === "en"
                                  ? "Roof"
                                  : "Tag"
                                : text)(
                              iconMap[substep.step]?.text ||
                                substep.label ||
                                substep.step,
                            )}
                          </span>
                        </CustomStepLabel>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Step>
      );
    });
  };

  // Always render the sidebar container to maintain width, even when loading or error
  return (
    <div className="w-56 h-auto flex flex-col border-r bg-sidebarColor m-3 rounded-xl relative">
      <div className="flex justify-center px-1 py-3 mt-2">
        <Link to="/">
          <img
            src={logo}
            alt="DroneTjek Logo"
            className="h-12 w-auto cursor-pointer"
          />
        </Link>
      </div>
      <div
        className="mt-6 px-4 overflow-y-auto pr-2 custom-scrollbar"
        style={{ maxHeight: "calc(100vh - 150px)" }}
      >
        {sidebarData === undefined ? (
          // Loader skeleton for stepper
          <div className="space-y-4 py-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-gray-200" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        ) : Array.isArray(sidebarData) && sidebarData.length > 0 ? (
          <div className="relative">
            <Stepper
              orientation="vertical"
              nonLinear
              activeStep={-1}
              connector={null}
              className="z-10 relative"
            >
              {renderSteps(sidebarData)}
            </Stepper>
          </div>
        ) : (
          <div className="text-gray-400 text-center py-8">
            {t("main.sidebar.noStepsAvailable")}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportSidebar;
